import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  getWallets,
  updateWalletStatus
} from '../../services/api';
import { 
  FiSearch, 
  FiFilter, 
  FiRefreshCw, 
  FiEye, 
  FiCheckCircle, 
  FiXCircle, 
  FiArrowLeft, 
  FiArrowRight,
  FiDownload,
  FiAlertCircle,
  FiMail,
  FiDollarSign,
  FiCreditCard,
  FiUser,
  FiPlus,
  FiMinus,
  FiActivity,
  FiLock,
  FiUnlock
} from 'react-icons/fi';
import { 
  TbArrowsExchange,
  TbWallet
} from 'react-icons/tb';

function Wallets() {
  const [wallets, setWallets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [filters, setFilters] = useState({
    status: '',
    min_balance: '',
    max_balance: '',
    search: ''
  });
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 20,
    total: 0,
    pages: 0
  });
  const [showFilters, setShowFilters] = useState(false);
  const [showAdjustmentModal, setShowAdjustmentModal] = useState(false);
  const [selectedWallet, setSelectedWallet] = useState(null);

  const navigate = useNavigate();

  useEffect(() => {
    fetchWallets();
  }, [filters, pagination.page]);

  const fetchWallets = async () => {
    try {
      setLoading(true);
      setError('');
      const params = {
        page: pagination.page,
        limit: pagination.limit,
        ...filters
      };

      // Remove empty filters
      Object.keys(params).forEach(key => {
        if (params[key] === '') {
          delete params[key];
        }
      });

      const response = await getWallets(params);
      if (response.success) {
        setWallets(response.data.wallets || []);
        setPagination(prev => ({
          ...prev,
          total: response.data.pagination?.total || 0,
          pages: response.data.pagination?.pages || 0
        }));
      } else {
        setError(response.message || 'Failed to load wallets');
      }
    } catch (err) {
      setError('Failed to load wallets: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  const handleStatusUpdate = async (walletId, newStatus, currentStatus) => {
    const action = newStatus === 'frozen' ? 'freeze' : 'unfreeze';
    if (!window.confirm(`Are you sure you want to ${action} this wallet?`)) {
      return;
    }

    try {
      const reason = prompt(`Enter reason for ${action} (optional):`, '');
      const response = await updateWalletStatus(walletId, newStatus, reason || '');

      if (response.success) {
        setSuccessMessage(`Wallet ${action}d successfully!`);
        setTimeout(() => setSuccessMessage(''), 3000);
        fetchWallets(); // Refresh the list
      } else {
        throw new Error(response.message || 'Update failed');
      }
    } catch (err) {
      setError(`Failed to ${action} wallet: ${err.message}`);
    }
  };

  const handlePageChange = (newPage) => {
    setPagination(prev => ({ ...prev, page: newPage }));
  };

  const getStatusBadge = (status) => {
    const baseClasses = "px-3 py-1 text-xs font-medium rounded-full flex items-center justify-center";
    
    const statusConfig = {
      'active': { bg: 'bg-green-100', text: 'text-green-800', label: 'Active', icon: FiCheckCircle },
      'frozen': { bg: 'bg-red-100', text: 'text-red-800', label: 'Frozen', icon: FiLock },
      'suspended': { bg: 'bg-yellow-100', text: 'text-yellow-800', label: 'Suspended', icon: FiAlertCircle },
      'inactive': { bg: 'bg-gray-200', text: 'text-gray-700', label: 'Inactive', icon: FiXCircle }
    };

    const config = statusConfig[status] || { bg: 'bg-gray-100', text: 'text-gray-800', label: status, icon: FiAlertCircle };
    const Icon = config.icon;
    
    return (
      <span className={`${baseClasses} ${config.bg} ${config.text}`}>
        <Icon className="mr-1" />
        {config.label}
      </span>
    );
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-NG', {
      style: 'currency',
      currency: 'NGN',
      minimumFractionDigits: 2
    }).format(amount || 0);
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  };

  const calculateNetFlow = (totalCredited, totalDebited) => {
    const credited = parseFloat(totalCredited) || 0;
    const debited = parseFloat(totalDebited) || 0;
    return credited - debited;
  };

  const clearFilters = () => {
    setFilters({
      status: '',
      min_balance: '',
      max_balance: '',
      search: ''
    });
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  // Loading Skeleton
  if (loading && wallets.length === 0) {
    return (
      <div className="min-h-screen bg-[#f5f5f5] p-6">
        <div className="animate-pulse">
          <div className="h-8 bg-gray-200 rounded w-1/4 mb-4"></div>
          <div className="h-4 bg-gray-200 rounded w-1/2 mb-8"></div>
          
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-6">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {[...Array(4)].map((_, i) => (
                <div key={i} className="h-10 bg-gray-100 rounded"></div>
              ))}
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
            {[...Array(5)].map((_, i) => (
              <div key={i} className="p-6 border-b border-gray-100">
                <div className="flex justify-between items-center">
                  <div className="space-y-2">
                    <div className="h-4 bg-gray-100 rounded w-48"></div>
                    <div className="h-3 bg-gray-100 rounded w-32"></div>
                  </div>
                  <div className="h-6 bg-gray-100 rounded w-20"></div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-full bg-[#f5f5f5] p-4 md:p-6">
      {/* Header */}
      <div className="mb-6">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl md:text-3xl font-bold text-[#0f0e0e]">Wallet Management</h1>
            <p className="text-gray-600 mt-1">Manage user wallets and balances</p>
          </div>
          <div className="flex gap-3">
            <button
              onClick={() => navigate('/admin/finance/reports')}
              className="px-4 py-2.5 bg-white text-[#0f0e0e] border border-gray-300 rounded-lg hover:bg-gray-50 transition-all duration-200 font-medium flex items-center"
            >
              <FiDownload className="mr-2" />
              Reports
            </button>
            <button
              onClick={fetchWallets}
              className="px-4 py-2.5 bg-[#ff660e] text-white rounded-lg hover:bg-[#e55c0d] transition-all duration-200 font-medium flex items-center"
            >
              <FiRefreshCw className="mr-2" />
              Refresh
            </button>
          </div>
        </div>
      </div>

      {/* Success Message */}
      {successMessage && (
        <div className="mb-6 bg-green-50 border border-green-200 rounded-xl p-4 flex items-center justify-between animate-fade-in">
          <div className="flex items-center space-x-3">
            <FiCheckCircle className="text-green-500 text-xl" />
            <p className="text-green-700 font-medium">{successMessage}</p>
          </div>
          <button 
            onClick={() => setSuccessMessage('')}
            className="text-green-700 hover:text-green-800 text-xl"
          >
            ×
          </button>
        </div>
      )}

      {/* Error Alert */}
      {error && (
        <div className="mb-6 bg-red-50 border border-red-200 rounded-xl p-4 flex items-center justify-between animate-fade-in">
          <div className="flex items-center space-x-3">
            <FiAlertCircle className="text-red-500 text-xl" />
            <p className="text-red-700">{error}</p>
          </div>
          <button 
            onClick={() => setError('')}
            className="text-red-700 hover:text-red-800 text-xl"
          >
            ×
          </button>
        </div>
      )}

      {/* Stats Summary */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-5">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-500 text-sm font-medium">Total Wallets</p>
              <p className="text-2xl font-bold text-[#0f0e0e] mt-1">{pagination.total}</p>
            </div>
            <div className="w-12 h-12 rounded-lg bg-blue-50 flex items-center justify-center">
              <TbWallet className="text-blue-600 text-2xl" />
            </div>
          </div>
        </div>
        
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-5">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-500 text-sm font-medium">Total Balance</p>
              <p className="text-2xl font-bold text-green-600 mt-1">
                {formatCurrency(wallets.reduce((sum, w) => sum + parseFloat(w.balance), 0))}
              </p>
            </div>
            <div className="w-12 h-12 rounded-lg bg-green-50 flex items-center justify-center">
              <FiDollarSign className="text-green-600 text-2xl" />
            </div>
          </div>
        </div>
        
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-5">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-500 text-sm font-medium">Active Wallets</p>
              <p className="text-2xl font-bold text-green-600 mt-1">
                {wallets.filter(w => w.status === 'active').length}
              </p>
            </div>
            <div className="w-12 h-12 rounded-lg bg-green-50 flex items-center justify-center">
              <FiCheckCircle className="text-green-600 text-2xl" />
            </div>
          </div>
        </div>
        
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-5">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-500 text-sm font-medium">Frozen Wallets</p>
              <p className="text-2xl font-bold text-red-600 mt-1">
                {wallets.filter(w => w.status === 'frozen').length}
              </p>
            </div>
            <div className="w-12 h-12 rounded-lg bg-red-50 flex items-center justify-center">
              <FiLock className="text-red-600 text-2xl" />
            </div>
          </div>
        </div>
      </div>

      {/* Filters Card */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 mb-6">
        <div className="p-4 border-b border-gray-100">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <FiFilter className="text-[#ff660e]" />
              <h3 className="font-medium text-[#0f0e0e]">Filters</h3>
            </div>
            <button
              onClick={() => setShowFilters(!showFilters)}
              className="text-sm text-[#ff660e] hover:text-[#e55c0d] font-medium flex items-center"
            >
              {showFilters ? 'Hide Filters' : 'Show Filters'}
            </button>
          </div>
        </div>

        {showFilters && (
          <div className="p-4 md:p-6">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              {/* Search */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Search</label>
                <div className="relative">
                  <FiSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                  <input
                    type="text"
                    placeholder="Name, email, store..."
                    value={filters.search}
                    onChange={(e) => handleFilterChange('search', e.target.value)}
                    className="w-full pl-10 pr-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#ff660e] focus:border-transparent transition-all duration-200"
                  />
                </div>
              </div>

              {/* Status Filter */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Status</label>
                <select
                  value={filters.status}
                  onChange={(e) => handleFilterChange('status', e.target.value)}
                  className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#ff660e] focus:border-transparent transition-all duration-200"
                >
                  <option value="">All Status</option>
                  <option value="active">Active</option>
                  <option value="frozen">Frozen</option>
                  <option value="suspended">Suspended</option>
                  <option value="inactive">Inactive</option>
                </select>
              </div>

              {/* Min Balance */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Min Balance</label>
                <input
                  type="number"
                  placeholder="0"
                  value={filters.min_balance}
                  onChange={(e) => handleFilterChange('min_balance', e.target.value)}
                  className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#ff660e] focus:border-transparent transition-all duration-200"
                />
              </div>

              {/* Max Balance */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Max Balance</label>
                <input
                  type="number"
                  placeholder="Any"
                  value={filters.max_balance}
                  onChange={(e) => handleFilterChange('max_balance', e.target.value)}
                  className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#ff660e] focus:border-transparent transition-all duration-200"
                />
              </div>
            </div>

            {/* Filter Actions */}
            <div className="flex justify-between items-center mt-6 pt-6 border-t border-gray-200">
              <button
                onClick={clearFilters}
                className="px-4 py-2 text-gray-600 hover:text-gray-800 text-sm font-medium hover:bg-gray-50 rounded-lg transition-colors"
              >
                Clear All Filters
              </button>
              <div className="flex gap-3">
                <button
                  onClick={() => setShowFilters(false)}
                  className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors font-medium"
                >
                  Cancel
                </button>
                <button
                  onClick={fetchWallets}
                  className="px-4 py-2 bg-[#ff660e] text-white rounded-lg hover:bg-[#e55c0d] transition-colors font-medium flex items-center"
                >
                  <FiFilter className="mr-2" />
                  Apply Filters
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Wallets Table */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Wallet & User
                </th>
                <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Balance Information
                </th>
                <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Transaction Stats
                </th>
                <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status & Date
                </th>
                <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-100">
              {wallets.map((wallet) => {
                const netFlow = calculateNetFlow(wallet.total_credited, wallet.total_debited);
                
                return (
                  <tr key={wallet.wallet_id} className="hover:bg-gray-50 transition-colors duration-150">
                    <td className="px-6 py-4">
                      <div>
                        <div className="flex items-center space-x-3 mb-2">
                          <div className="w-10 h-10 rounded-full bg-gradient-to-r from-[#ff660e] to-orange-400 flex items-center justify-center text-white font-bold">
                            {wallet.first_name?.charAt(0)}{wallet.last_name?.charAt(0)}
                          </div>
                          <div>
                            <div className="text-sm font-medium text-[#0f0e0e]">
                              {wallet.first_name} {wallet.last_name}
                            </div>
                            <div className="text-xs text-gray-500">
                              ID: {wallet.user_id}
                            </div>
                          </div>
                        </div>
                        <div className="text-sm text-gray-600">
                          <FiMail className="inline mr-2 text-gray-400" />
                          {wallet.email}
                        </div>
                        {wallet.store_name && (
                          <div className="text-xs text-gray-500 mt-1 truncate max-w-xs">
                            🏪 {wallet.store_name}
                          </div>
                        )}
                        <div className="text-xs text-gray-400 mt-1">
                          Wallet ID: {wallet.wallet_id}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="space-y-2">
                        <div className="text-2xl font-bold text-[#0f0e0e]">
                          {formatCurrency(wallet.balance)}
                        </div>
                        <div className="text-xs text-gray-500 space-y-1">
                          <div className="flex justify-between">
                            <span>Currency:</span>
                            <span className="font-medium">{wallet.currency}</span>
                          </div>
                          <div className="flex justify-between">
                            <span>Created:</span>
                            <span>{formatDate(wallet.created_at)}</span>
                          </div>
                          <div className="flex justify-between">
                            <span>Last Updated:</span>
                            <span>{formatDate(wallet.updated_at)}</span>
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="space-y-3">
                        <div className="text-sm">
                          <div className="flex justify-between mb-1">
                            <span className="text-gray-500">Transactions:</span>
                            <span className="font-medium">{wallet.transaction_count}</span>
                          </div>
                          <div className="flex justify-between text-green-600">
                            <span>Total Credited:</span>
                            <span className="font-medium">+{formatCurrency(wallet.total_credited)}</span>
                          </div>
                          <div className="flex justify-between text-red-600">
                            <span>Total Debited:</span>
                            <span className="font-medium">-{formatCurrency(wallet.total_debited)}</span>
                          </div>
                        </div>
                        <div className="pt-2 border-t border-gray-100">
                          <div className="flex justify-between">
                            <span className="text-sm text-gray-500">Net Flow:</span>
                            <span className={`font-medium ${netFlow >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                              {netFlow >= 0 ? '+' : ''}{formatCurrency(netFlow)}
                            </span>
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex flex-col items-start space-y-2">
                        {getStatusBadge(wallet.status)}
                        <div className="text-sm text-gray-500">
                          Created: {formatDate(wallet.created_at)}
                        </div>
                        <div className="text-xs text-gray-400">
                          Last Active: {formatDate(wallet.updated_at)}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex flex-col space-y-2 min-w-[180px]">
                        <button
                          onClick={() => navigate(`/admin/finance/wallets/${wallet.wallet_id}`)}
                          className="px-3 py-1.5 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg text-sm font-medium flex items-center justify-center transition-colors"
                        >
                          <FiEye className="mr-2" />
                          View Details
                        </button>
                        
                        <div className="flex flex-wrap gap-1">
                          {wallet.status === 'active' ? (
                            <button
                              onClick={() => handleStatusUpdate(wallet.wallet_id, 'frozen', wallet.status)}
                              className="px-3 py-1 bg-red-50 hover:bg-red-100 text-red-700 rounded text-xs font-medium border border-red-200 flex-1"
                            >
                              <FiLock className="inline mr-1" />
                              Freeze
                            </button>
                          ) : wallet.status === 'frozen' ? (
                            <button
                              onClick={() => handleStatusUpdate(wallet.wallet_id, 'active', wallet.status)}
                              className="px-3 py-1 bg-green-50 hover:bg-green-100 text-green-700 rounded text-xs font-medium border border-green-200 flex-1"
                            >
                              <FiUnlock className="inline mr-1" />
                              Unfreeze
                            </button>
                          ) : null}
                          
                          <button
                            onClick={() => {
                              setSelectedWallet(wallet);
                              setShowAdjustmentModal(true);
                            }}
                            className="px-3 py-1 bg-blue-50 hover:bg-blue-100 text-blue-700 rounded text-xs font-medium border border-blue-200 flex-1"
                          >
                            <TbArrowsExchange className="inline mr-1" />
                            Adjust
                          </button>
                        </div>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {/* Empty State */}
        {wallets.length === 0 && !loading && (
          <div className="text-center py-16">
            <div className="text-gray-300 text-8xl mb-6">💳</div>
            <h3 className="text-xl font-medium text-gray-700 mb-2">No wallets found</h3>
            <p className="text-gray-500 mb-6">Try adjusting your search or filters to find what you're looking for.</p>
            <button
              onClick={clearFilters}
              className="px-6 py-2.5 bg-[#ff660e] text-white rounded-lg hover:bg-[#e55c0d] transition-colors font-medium"
            >
              Clear Filters
            </button>
          </div>
        )}

        {/* Pagination */}
        {pagination.pages > 1 && (
          <div className="bg-gray-50 px-6 py-4 border-t border-gray-200">
            <div className="flex flex-col md:flex-row items-center justify-between gap-4">
              <div className="text-sm text-gray-700">
                Showing <span className="font-semibold">{(pagination.page - 1) * pagination.limit + 1}</span> to{' '}
                <span className="font-semibold">
                  {Math.min(pagination.page * pagination.limit, pagination.total)}
                </span>{' '}
                of <span className="font-semibold">{pagination.total}</span> wallets
              </div>
              <div className="flex items-center space-x-2">
                <button
                  onClick={() => handlePageChange(pagination.page - 1)}
                  disabled={pagination.page === 1}
                  className="px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
                >
                  <FiArrowLeft className="mr-2" />
                  Previous
                </button>
                
                <div className="flex items-center space-x-1">
                  {Array.from({ length: Math.min(5, pagination.pages) }, (_, i) => {
                    let pageNum;
                    if (pagination.pages <= 5) {
                      pageNum = i + 1;
                    } else if (pagination.page <= 3) {
                      pageNum = i + 1;
                    } else if (pagination.page >= pagination.pages - 2) {
                      pageNum = pagination.pages - 4 + i;
                    } else {
                      pageNum = pagination.page - 2 + i;
                    }
                    
                    return (
                      <button
                        key={pageNum}
                        onClick={() => handlePageChange(pageNum)}
                        className={`w-10 h-10 rounded-lg text-sm font-medium ${
                          pagination.page === pageNum
                            ? 'bg-[#ff660e] text-white'
                            : 'border border-gray-300 text-gray-700 hover:bg-gray-50'
                        }`}
                      >
                        {pageNum}
                      </button>
                    );
                  })}
                </div>
                
                <button
                  onClick={() => handlePageChange(pagination.page + 1)}
                  disabled={pagination.page === pagination.pages}
                  className="px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
                >
                  Next
                  <FiArrowRight className="ml-2" />
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Adjustment Modal */}
      {showAdjustmentModal && selectedWallet && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-xl shadow-lg max-w-md w-full">
            <div className="p-6">
              <h3 className="text-lg font-bold text-[#0f0e0e] mb-4">
                Manual Wallet Adjustment
              </h3>
              
              <div className="mb-4 p-3 bg-gray-50 rounded-lg">
                <p className="text-sm text-gray-600">
                  Wallet: <span className="font-medium">{selectedWallet.first_name} {selectedWallet.last_name}</span>
                </p>
                <p className="text-sm text-gray-600">
                  Current Balance: <span className="font-bold">{formatCurrency(selectedWallet.balance)}</span>
                </p>
              </div>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Adjustment Type
                  </label>
                  <select className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#ff660e] focus:border-transparent">
                    <option value="credit">Credit (+ Add Funds)</option>
                    <option value="debit">Debit (- Remove Funds)</option>
                    <option value="bonus">Bonus (Free Credit)</option>
                    <option value="penalty">Penalty (Deduction)</option>
                  </select>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Amount (NGN)
                  </label>
                  <input
                    type="number"
                    placeholder="0.00"
                    className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#ff660e] focus:border-transparent"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Reason
                  </label>
                  <input
                    type="text"
                    placeholder="e.g., Manual adjustment, Refund, etc."
                    className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#ff660e] focus:border-transparent"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Description (Optional)
                  </label>
                  <textarea
                    placeholder="Additional details..."
                    rows="2"
                    className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#ff660e] focus:border-transparent"
                  />
                </div>
              </div>
              
              <div className="flex justify-end space-x-3 mt-6 pt-6 border-t border-gray-200">
                <button
                  onClick={() => setShowAdjustmentModal(false)}
                  className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors font-medium"
                >
                  Cancel
                </button>
                <button
                  onClick={() => {
                    // Implement adjustment logic here
                    setShowAdjustmentModal(false);
                  }}
                  className="px-4 py-2 bg-[#ff660e] text-white rounded-lg hover:bg-[#e55c0d] transition-colors font-medium"
                >
                  Apply Adjustment
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* CSS for animations */}
      <style jsx>{`
        @keyframes fade-in {
          from { opacity: 0; transform: translateY(-10px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .animate-fade-in {
          animation: fade-in 0.3s ease-out;
        }
      `}</style>
    </div>
  );
}

export default Wallets;